import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { GAMES, GAME_KEYS, nextDraw, returnForJackpot } from '../engine';
import type { GameKey } from '../engine';
import { fetchDashboard } from '../lib/api';
import type { Dashboard as DashboardData, Provenance } from '../lib/api';
import {
  formatDateLong,
  formatDateShort,
  formatJackpot,
  formatOdds,
  formatPercent,
  relativeDays,
} from '../lib/format';
import { Balls } from '../components/Balls';
import { Countdown } from '../components/Countdown';

const PROVENANCE_LABEL: Record<Provenance, string> = {
  network: 'à jour',
  cache: 'hors ligne — dernier instantané connu',
  seed: 'instantané embarqué — aucune ingestion configurée',
};

/**
 * Refreshes when the app is opened, and only then. iOS runs no reliable background
 * task for a PWA, so the app never claims to fetch results on its own.
 */
export function Dashboard(): ReactElement {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const load = (bypassCache = false): void => {
      setLoading(true);
      void fetchDashboard(bypassCache).then((d) => {
        if (!alive) return;
        setData(d);
        setLoading(false);
        // The server answers before it has finished fetching a missing draw, so
        // when it says it is working, look again shortly. Two tries: if the FDJ
        // has not published yet, the next app opening will do.
        if (d.refreshing.length > 0) {
          timers.push(setTimeout(() => load(true), 4000));
          timers.push(setTimeout(() => load(true), 12_000));
        }
      });
    };

    load();
    // Re-check when the app comes back to the foreground. On iOS this is the only
    // moment the app is allowed to do anything, so it is where the refresh lives.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      for (const t of timers) clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Order the cards by how soon each game draws next.
  const ordered = useMemo<GameKey[]>(() => {
    const now = new Date();
    return [...GAME_KEYS].sort(
      (a, b) => nextDraw(GAMES[a], now).msUntil - nextDraw(GAMES[b], now).msUntil,
    );
  }, []);

  return (
    <div className="screen">
      <h1>Tirages</h1>
      <p className="lede">
        {loading && data === null
          ? 'Chargement…'
          : data === null
            ? 'Aucune donnée.'
            : PROVENANCE_LABEL[data.provenance]}
      </p>

      {data?.provenance === 'seed' && (
        <div className="notice">
          Le Worker d’ingestion n’est pas configuré : l’application affiche l’historique
          embarqué à la compilation. Définissez <code>VITE_API_BASE</code> puis déployez le
          Worker pour recevoir les tirages du soir.
        </div>
      )}

      {ordered.map((key) => {
        const g = GAMES[key];
        const next = nextDraw(g);
        const latest = data?.latest[key] ?? null;
        const refreshing = data?.refreshing.includes(key) ?? false;
        return (
          <div key={key} className={`card${next.today ? ' highlight' : ''}`}>
            <div className="card-head">
              <span className="card-title">{g.label}</span>
              {refreshing ? (
                <span className="pill muted">récupération du résultat…</span>
              ) : next.today ? (
                <span className="pill">tirage ce soir</span>
              ) : (
                <span className="pill muted">{formatDateShort(next.date)}</span>
              )}
            </div>

            {latest === null ? (
              <p className="dim small">Aucun tirage connu.</p>
            ) : (
              <>
                <div className="card-sub">
                  {formatDateLong(latest.date)} · {relativeDays(latest.date)}
                  {refreshing && ' — un tirage plus récent est en cours de récupération'}
                </div>
                <Balls main={latest.main} bonus={latest.bonus} bonusLabel={g.bonusLabel} />
              </>
            )}

            <div className="metrics">
              <span className="metric">
                prochain tirage <b>{formatDateLong(next.date)}</b>
              </span>
              <span className="metric">
                dans <b><Countdown target={next.at} /></b>
              </span>
            </div>
            <div className="metrics" style={{ marginTop: 4 }}>
              <span className="metric">
                rang 1 <b>{formatOdds(g.topOdds)}</b>
              </span>
              <span className="metric">
                lots{' '}
                <b>{g.sharingExposure >= 0.5 ? 'parimutuels' : 'à montants fixes'}</b>
              </span>
              {latest !== null && latest.jackpotCents !== null && latest.jackpotCents > 0 && (
                <span className="metric" title="Montant annoncé au rang 1 de ce tirage">
                  jackpot annoncé <b>{formatJackpot(latest.jackpotCents)}</b>
                </span>
              )}
            </div>

            {(() => {
              // The only number in this application that can inform a decision about
              // whether to play — never about which numbers to play. Shown solely
              // where the operator publishes the announced jackpot on every draw.
              const band = returnForJackpot(key, latest?.jackpotCents ?? null);
              if (band === null) return null;
              return (
                <div className="notice" style={{ marginBottom: 0 }}>
                  À ce niveau de jackpot, le Loto a historiquement redistribué{' '}
                  <strong>{formatPercent(band.rtp, 0)}</strong> des mises, contre{' '}
                  {formatPercent(g.measuredRtp, 0)} en moyenne tous tirages confondus.
                  {band.deltaVsAverage > 0.02 && ' Un jackpot reporté contient les mises des tirages précédents.'}
                  {' '}Cela reste sous 100 % : la perte est réduite, jamais annulée.
                </div>
              );
            })()}

            {g.key === 'eurodreams' && (
              <div className="notice warn" style={{ marginBottom: 0 }}>
                Lots à montant fixe. Éviter les combinaisons populaires ne rapporte
                quasiment rien ici : <strong>+0,4 % réels</strong>, contre +66 % au Loto.
              </div>
            )}
          </div>
        );
      })}

      <p className="footnote">
        L’application se rafraîchit à l’ouverture. iOS n’autorise pas de tâche de fond
        fiable pour une application web : elle n’ira pas chercher les résultats toute
        seule, et ne vous enverra pas de notification de tirage.
      </p>
    </div>
  );
}
